import type { ChecklistStatus } from '../../shared/checklistUtils';
import { formatBooleanDataPointValue, formatLoadedDataPointValue, getStatusLabel } from '../../shared/format';

import type { TaxesPaymentsChecklistRow } from './types';

export function buildTaxesPaymentsRows({
  cityOtherTax,
  cityTaxComments,
  hasPropertyTaxesPayableAtProperty,
  opportunityDataAvailable,
  paymentMethod,
  paymentMilestone,
  prizeOrderId,
  propertyDataAvailable,
}: {
  cityOtherTax?: boolean;
  cityTaxComments?: string;
  hasPropertyTaxesPayableAtProperty: boolean;
  opportunityDataAvailable: boolean;
  paymentMethod?: string;
  paymentMilestone?: string;
  prizeOrderId?: string;
  propertyDataAvailable: boolean;
}): Array<TaxesPaymentsChecklistRow> {
  const cityTaxStatus = buildCityTaxStatus({ cityOtherTax, cityTaxComments, opportunityDataAvailable });
  const perRoomTaxStatus = buildPerRoomTaxStatus({
    cityOtherTax,
    hasPropertyTaxesPayableAtProperty,
    opportunityDataAvailable,
    propertyDataAvailable,
  });
  const paymentStatus = buildPaymentMechanismStatus({ opportunityDataAvailable, paymentMethod, paymentMilestone });
  const prizeStatus = buildPrizeStatus({ opportunityDataAvailable, prizeOrderId });

  return [
    {
      details: [
        {
          label: 'City/Other Tax Payable Direct',
          value: formatBooleanDataPointValue({ dataAvailable: opportunityDataAvailable, enabled: cityOtherTax }),
        },
        { label: 'Details', value: cityTaxComments?.trim() || 'Not provided' },
      ],
      field: 'City/Other Tax Payable Direct',
      id: 'city-tax-payable-direct',
      name: 'City/Other Tax Payable Direct',
      rule: 'If City/Other Tax Payable Direct is marked Yes, the tax details field must be populated before approval.',
      status: cityTaxStatus,
      statusLabel: getStatusLabel(cityTaxStatus),
      summary: getSummary(cityTaxStatus, {
        approved: 'City/Other Tax Payable Direct details are complete.',
        blocked: 'Opportunity data is unavailable to verify this rule.',
        pending: 'City/Other Tax Payable Direct needs tax detail comments before approval.',
      }),
    },
    {
      details: [
        {
          label: 'City/Other Tax Payable Direct',
          value: formatBooleanDataPointValue({ dataAvailable: opportunityDataAvailable, enabled: cityOtherTax }),
        },
        {
          label: 'Per-room city taxes payable at property',
          value: formatLoadedDataPointValue({
            dataAvailable: propertyDataAvailable,
            loaded: hasPropertyTaxesPayableAtProperty,
          }),
        },
      ],
      field: 'Per-room city taxes in Admin Portal',
      id: 'per-room-city-taxes',
      name: 'Per-room city taxes in Admin Portal',
      rule: 'If per-room city taxes are payable directly to the property, they must be loaded in the Admin Portal before the offer goes live.',
      status: perRoomTaxStatus,
      statusLabel: getStatusLabel(perRoomTaxStatus),
      summary: getSummary(perRoomTaxStatus, {
        approved: 'Per-room city taxes are correctly reflected in the Admin Portal.',
        blocked: 'Opportunity or property data is unavailable to verify this rule.',
        pending: 'Per-room city taxes need to be loaded in the Admin Portal.',
      }),
    },
    {
      details: [
        { label: 'Payment method', value: paymentMethod?.trim() || 'Not provided' },
        { label: 'Milestone rules', value: paymentMilestone?.trim() || 'Not provided' },
      ],
      field: 'Payment mechanism and milestone rules',
      id: 'payment-mechanism-milestone',
      name: 'Payment mechanism and milestone rules',
      rule: 'Both payment method and milestone rules must be populated, and milestone percentages must sum to 100%.',
      status: paymentStatus,
      statusLabel: getStatusLabel(paymentStatus),
      summary: getSummary(paymentStatus, {
        approved: 'Payment method and milestone rules are complete and sum to 100%.',
        blocked: 'Opportunity data is unavailable to verify this rule.',
        pending: 'Payment method and milestone rules need review.',
      }),
    },
    {
      details: [{ label: 'Prize order ID', value: prizeOrderId?.trim() || 'None' }],
      field: 'Prize / Prize Used',
      id: 'prize-used',
      name: 'Prize / Prize Used',
      rule: 'If a prize order ID is present, the offer requires CM team approval before going live.',
      status: prizeStatus,
      statusLabel: getStatusLabel(prizeStatus),
      summary: getSummary(prizeStatus, {
        approved: 'No prize order requiring additional CM approval.',
        blocked: 'Opportunity data is unavailable to verify this rule.',
        pending: 'Prize order present — CM team approval required.',
      }),
    },
  ];
}

function getSummary(status: ChecklistStatus, copy: { approved: string; blocked: string; pending: string }): string {
  if (status === 'approved') {
    return copy.approved;
  }
  if (status === 'blocked') {
    return copy.blocked;
  }
  return copy.pending;
}

function buildCityTaxStatus({
  cityOtherTax,
  cityTaxComments,
  opportunityDataAvailable,
}: {
  cityOtherTax?: boolean;
  cityTaxComments?: string;
  opportunityDataAvailable: boolean;
}): ChecklistStatus {
  if (!opportunityDataAvailable) {
    return 'blocked';
  }
  if (!cityOtherTax) {
    return 'approved';
  }
  return cityTaxComments?.trim() ? 'approved' : 'requires approval';
}

function buildPerRoomTaxStatus({
  cityOtherTax,
  hasPropertyTaxesPayableAtProperty,
  opportunityDataAvailable,
  propertyDataAvailable,
}: {
  cityOtherTax?: boolean;
  hasPropertyTaxesPayableAtProperty: boolean;
  opportunityDataAvailable: boolean;
  propertyDataAvailable: boolean;
}): ChecklistStatus {
  if (!opportunityDataAvailable || !propertyDataAvailable) {
    return 'blocked';
  }
  if (!cityOtherTax) {
    return 'approved';
  }
  return hasPropertyTaxesPayableAtProperty ? 'approved' : 'requires approval';
}

function buildPaymentMechanismStatus({
  opportunityDataAvailable,
  paymentMethod,
  paymentMilestone,
}: {
  opportunityDataAvailable: boolean;
  paymentMethod?: string;
  paymentMilestone?: string;
}): ChecklistStatus {
  if (!opportunityDataAvailable) {
    return 'blocked';
  }
  if (!paymentMethod?.trim() || !paymentMilestone?.trim()) {
    return 'requires approval';
  }
  const milestoneTotal = sumMilestonePercentages(paymentMilestone);
  return milestoneTotal !== undefined && Math.abs(milestoneTotal - 100) < 0.01 ? 'approved' : 'requires approval';
}

function buildPrizeStatus({
  opportunityDataAvailable,
  prizeOrderId,
}: {
  opportunityDataAvailable: boolean;
  prizeOrderId?: string;
}): ChecklistStatus {
  if (!opportunityDataAvailable) {
    return 'blocked';
  }
  return prizeOrderId?.trim() ? 'requires approval' : 'approved';
}

function sumMilestonePercentages(milestone: string): number | undefined {
  const matches = [...milestone.matchAll(/(\d+(?:\.\d+)?)\s*%/g)];
  if (matches.length > 0) {
    return matches.reduce((sum, milestoneMatch) => sum + parseFloat(milestoneMatch[1]), 0);
  }

  const bareMilestoneValues = extractBareMilestoneValues(milestone);
  if (bareMilestoneValues.length === 0) {
    return undefined;
  }
  return bareMilestoneValues.reduce((sum, milestoneValue) => sum + milestoneValue, 0);
}

function extractBareMilestoneValues(milestone: string): Array<number> {
  return milestone
    .split(/[\n;,]+/)
    .map((milestonePart) => milestonePart.trim())
    .filter(Boolean)
    .map(extractBareMilestoneValue)
    .filter(
      (milestoneValue): milestoneValue is number => milestoneValue !== undefined && !Number.isNaN(milestoneValue),
    );
}

function extractBareMilestoneValue(milestonePart: string): number | undefined {
  const valueText = milestonePart.split(/\s[-:]\s|:\s|-\s/).pop() ?? milestonePart;
  return getLastNonTimingNumber(valueText) ?? getLastNonTimingNumber(milestonePart);
}

function getLastNonTimingNumber(valueText: string): number | undefined {
  const numericValues = [...valueText.matchAll(/(\d+(?:\.\d+)?)(?:\s*(day|days|month|months))?/gi)]
    .filter(([, , timeUnit]) => !timeUnit)
    .map(([, numberText]) => Number(numberText));

  return numericValues.length > 0 ? numericValues[numericValues.length - 1] : undefined;
}
